import { useState, useMemo, useRef } from 'react';
import { FolderIcon, HomeIcon } from '@heroicons/react/24/outline';
import ModalWrapper from '../ModalWrapper';
import { CommonFooter } from '../FormComponent';
import { flattenFolders, getDescendantIds } from './helpers';
import styles from '../../styles/assetsManagement/filesManager.module.scss';

const MoveFolderModal = ({ folder, rawFolders, onConfirm, onClose, loading }) => {
  const [selectedParentId, setSelectedParentId] = useState(folder.parentFolderId ?? 'root');
  const itemRefs = useRef([]);

  const disabledIds = useMemo(
    () => getDescendantIds(folder.id, rawFolders),
    [folder.id, rawFolders],
  );

  const pickable = useMemo(() => {
    const all = flattenFolders(rawFolders);
    return all.filter((f) => !disabledIds.has(f.id) && f.level < 2);
  }, [rawFolders, disabledIds]);

  const isUnchanged = selectedParentId === (folder.parentFolderId ?? 'root');

  const handleKeyDown = (e, idx) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      itemRefs.current[Math.min(idx + 1, pickable.length)]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      itemRefs.current[Math.max(idx - 1, 0)]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelectedParentId(idx === 0 ? 'root' : pickable[idx - 1].id);
    }
  };

  const buttons = [
    { label: 'Cancel', variant: 'outline', className: 'primary', onClick: onClose, disabled: loading },
    {
      label: loading ? 'Moving…' : 'Move Here',
      variant: 'primary',
      className: 'white bg-blue600',
      onClick: () => onConfirm(selectedParentId === 'root' ? null : selectedParentId),
      disabled: loading || isUnchanged,
    },
  ];

  return (
    <ModalWrapper
      isOpen
      onClose={onClose}
      title="Move Folder"
      size="small"
      footer={<CommonFooter buttons={buttons} />}
    >
      <div className={styles.moveModalBody}>
        <div className={styles.movingFromRow}>
          <span className={styles.movingLabel}>Moving:</span>
          <div className={styles.movingFolder}>
            <FolderIcon className={styles.movingFolderIcon} />
            <span className={styles.movingFolderName} title={folder.folderPath}>{folder.name}</span>
          </div>
        </div>

        <p className={styles.movePickerLabel}>Select destination:</p>

        <div
          className={styles.folderPickerList}
          role="radiogroup"
          aria-label="Select destination folder"
        >
          <div
            ref={(el) => { itemRefs.current[0] = el; }}
            className={[
              styles.folderPickerItem,
              selectedParentId === 'root' ? styles.folderPickerSelected : '',
            ].join(' ')}
            role="radio"
            aria-checked={selectedParentId === 'root'}
            tabIndex={0}
            onClick={() => setSelectedParentId('root')}
            onKeyDown={(e) => handleKeyDown(e, 0)}
          >
            <HomeIcon className={styles.pickerIcon} />
            <span className={styles.pickerItemName}>Root level (no parent)</span>
            {selectedParentId === 'root' && (
              <span className={styles.pickerCheck} aria-hidden="true">✓</span>
            )}
          </div>

          {pickable.map((f, i) => {
            const isSelected = selectedParentId === f.id;
            return (
              <div
                key={f.id}
                ref={(el) => { itemRefs.current[i + 1] = el; }}
                className={[
                  styles.folderPickerItem,
                  isSelected ? styles.folderPickerSelected : '',
                ].join(' ')}
                style={{ paddingLeft: `${f.level * 20 + 12}px` }}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                onClick={() => setSelectedParentId(f.id)}
                onKeyDown={(e) => handleKeyDown(e, i + 1)}
                title={f.folderPath}
              >
                <FolderIcon className={styles.pickerIcon} />
                <span className={styles.pickerItemName}>{f.folderName}</span>
                {f.level === 0 && <span className={styles.pickerLevelBadge}>L0</span>}
                {f.level === 1 && <span className={styles.pickerLevelBadge}>L1</span>}
                {isSelected && <span className={styles.pickerCheck} aria-hidden="true">✓</span>}
              </div>
            );
          })}

          {pickable.length === 0 && (
            <p className={styles.pickerEmpty}>No other folders available at this level.</p>
          )}
        </div>
      </div>
    </ModalWrapper>
  );
};

export default MoveFolderModal;
